import { KeyboardEvent, PointerEvent, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { cn } from 'utils/cn';
import { COLUMN_MIN_WIDTH } from './commentsDock';
import type { CommentsSession } from './commentsSession';
import { InvoiceCommentsView } from './InvoiceCommentsView';

const KEY_STEP = 16;

type Props = {
    invoiceId: number;
    linkedCommentId: number | null;
    session: CommentsSession;
    /** The width the user set, drawn at most at `maxWidth`. */
    width: number;
    maxWidth: number;
    onResize: (width: number) => void;
    onUndock: () => void;
    onClose: () => void;
    focusOnShow: 'heading' | 'dock' | null;
};

/**
 * The comments docked as a column of the invoice page, beside the invoice area: a landmark, not a dialog, so the
 * page around it stays usable and Escape leaves it open. Its left edge is the grip that sets its width.
 */
export function InvoiceCommentsColumn({
    invoiceId,
    linkedCommentId,
    session,
    width,
    maxWidth,
    onResize,
    onUndock,
    onClose,
    focusOnShow,
}: Props) {
    const intl = useIntl();
    const isNewDesign = useIsNewDesign();
    const drag = useRef<{ x: number; width: number; next: number } | null>(null);
    // The width mid-drag stays here and is handed up once, on release: the invoice page renders once per drag.
    const [dragWidth, setDragWidth] = useState<number | null>(null);
    const drawn = dragWidth ?? Math.min(Math.max(width, COLUMN_MIN_WIDTH), maxWidth);
    const clamp = (next: number) => Math.round(Math.min(Math.max(next, COLUMN_MIN_WIDTH), maxWidth));

    const startDrag = (event: PointerEvent<HTMLSpanElement>) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        drag.current = { x: event.clientX, width: drawn, next: drawn };
        setDragWidth(drawn);
    };
    // The grip is the left edge: moving it left widens the column.
    const moveDrag = (event: PointerEvent<HTMLSpanElement>) => {
        if (!drag.current) return;
        drag.current.next = clamp(drag.current.width + drag.current.x - event.clientX);
        setDragWidth(drag.current.next);
    };
    const endDrag = () => {
        // A press that moves nothing leaves the remembered width alone, even one drawn narrower for want of room.
        if (drag.current && drag.current.next !== drag.current.width) onResize(drag.current.next);
        drag.current = null;
        setDragWidth(null);
    };
    const nudge = (event: KeyboardEvent<HTMLSpanElement>) => {
        const step = event.key === 'ArrowLeft' ? KEY_STEP : event.key === 'ArrowRight' ? -KEY_STEP : 0;
        if (step === 0) return;
        event.preventDefault();
        // A key that moves nothing leaves the remembered width alone, as a press does.
        const next = clamp(drawn + step);
        if (next !== drawn) onResize(next);
    };

    return (
        <aside
            aria-label={intl.formatMessage({ id: 'invoice-comments.title' })}
            style={{ width: drawn }}
            className={cn(
                'relative flex min-h-0 shrink-0 flex-col',
                isNewDesign ? 'bg-paper shadow-panel rounded-2xl outline-hidden' : 'bg-paper border-divider border-s'
            )}
        >
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[inherit]">
                <InvoiceCommentsView
                    invoiceId={invoiceId}
                    onClose={onClose}
                    linkedCommentId={linkedCommentId}
                    session={session}
                    host="column"
                    dock={{ docked: true, onToggle: onUndock }}
                    focusOnShow={focusOnShow}
                />
            </div>
            {/* After the view, so it is drawn over the column's edge without a z-index of its own. */}
            <span
                role="separator"
                tabIndex={0}
                aria-orientation="vertical"
                aria-label={intl.formatMessage({ id: 'invoice-comments.width' })}
                aria-valuenow={drawn}
                aria-valuemin={COLUMN_MIN_WIDTH}
                aria-valuemax={Math.round(maxWidth)}
                data-dragging={dragWidth !== null ? '' : undefined}
                onPointerDown={startDrag}
                onPointerMove={moveDrag}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                onKeyDown={nudge}
                className="focus-visible:bg-primary data-dragging:bg-primary absolute inset-y-0 -left-1 w-2 cursor-ew-resize touch-none rounded-full outline-none"
            />
        </aside>
    );
}
